import React, { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import Paginate from "../../../components/shared/paginate";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../../components/shared/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/shared/alertDialog";

import {
  CreateAdmin,
  DeleteAdmin,
  FetchAdmins,
  UpdateAdmin,
} from "../../../apis/admin";
import { FetchRoles } from "../../../apis/adminRoles";
import { Eye, EyeOff, X } from "lucide-react";
import { AxiosError } from "axios";

import { set, z } from "zod";
// Define the Zod schema for validation
const adminUserSchema = z.object({
  email: z
    .string()
    .min(1, { message: "This field has to be filled." })
    .email("This is not a valid email."),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters long." })
    .regex(/[A-Za-z]/, "Password must contain alphabets")
    .regex(/[0-9]/, "Password must contain numerics"),
  roleId: z.string().min(1, { message: "Role need to be selected" }),
});

const ManageAdminUsers = () => {
  // pagination
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  // alert dialog
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [admins, setAdmins] = useState([]);
  // dialog
  const [dialogTitle, setDialogTitle] = useState("");
  const [adminId, setAdminId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [openDialog, setOpenDialog] = useState("");
  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  const limit = "10";
  const fetchAdmins = async () => {
    try {
      const response = await FetchAdmins(currentPage, limit);
      setAdmins(response.data.users);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      toast.error("Error fetching admin users");
    }
  };
  const fetchRoles = async () => {
    try {
      const response = await FetchRoles("1", "100");
      setRoles(response.data.roles);
    } catch (error) {
      toast.error("Error fetching admin roles");
    }
  };
  useEffect(() => {
    fetchAdmins();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handelDialogNew = () => {
    setAdminId("");
    setRoleId("");
    setEmail("");
    setPassword("");
    setDialogTitle("Add admin user");
    setShowPassword(false);
    setErrors([]);
    setOpenDialog(true);
  };

  const handleRoleDelete = async (adminId) => {
    try {
      const result = await DeleteAdmin(adminId);
      if (result.success) {
        setAdmins(
          admins.filter((admin) => {
            if (admin._id !== adminId) return admin;
          }),
        );
        toast.success("Admin deleted");
      }
    } catch (err) {
      toast.error("Error deleting admin");
    }
  };

  const updateAdmin = async () => {
    try {
      const validationResult = adminUserSchema.safeParse({
        email,
        password,
        roleId,
      });

      if (!validationResult.success) {
        const fieldErrors = {};
        validationResult.error.errors.forEach((error) => {
          if (error.path.includes("email")) {
            fieldErrors.email = error.message;
          } else if (error.path.includes("password")) {
            fieldErrors.password = error.message;
          } else if (error.path.includes("roleId")) {
            fieldErrors.roleId = error.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }

      const result = await UpdateAdmin(adminId, email, password, roleId);
      if (result) {
        fetchAdmins();
        setOpenDialog(false);
        toast.success("Admin user updated");
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response.status === 400) {
          if (
            err.response.data &&
            err.response.data.errors &&
            err.response.data.errors.length > 0
          ) {
            err.response.data.errors.forEach((error) => {
              toast.error(error.msg);
            });
          }
        }
      } else {
        console.log("err", err);
        toast.error("Error updating admin user");
      }
    }
  };

  const addAdmin = async () => {
    try {
      const validationResult = adminUserSchema.safeParse({
        email,
        password,
        roleId,
      });

      if (!validationResult.success) {
        const fieldErrors = {};
        validationResult.error.errors.forEach((error) => {
          if (error.path.includes("email")) {
            fieldErrors.email = error.message;
          } else if (error.path.includes("password")) {
            fieldErrors.password = error.message;
          } else if (error.path.includes("roleId")) {
            fieldErrors.roleId = error.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }
      const result = await CreateAdmin(email, password, roleId);
      if (result) {
        fetchAdmins();
        setOpenDialog(false);
        toast.success("Admin user added");
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response.status === 400) {
          if (
            err.response.data &&
            err.response.data.errors &&
            err.response.data.errors.length > 0
          ) {
            err.response.data.errors.forEach((error) => {
              toast.error(error.msg);
            });
          }
        }
      } else {
        console.log("err", err);
        toast.error("Error updating admin user");
      }
    }

    // if (email && password && roleId) {
    //   try {
    // const result = await CreateAdmin(email, password, roleId);
    // if (result) {
    //   fetchAdmins();
    //   setOpenDialog(false);
    //   toast.success("Admin user added");
    // }
    //   } catch (err) {
    //     setOpenDialog(false);
    //     toast.error("Error adding admin user");
    //   }
    // }
  };

  const handelDialogUpdate = (admin) => {
    setAdminId(admin._id);
    setEmail(admin.email);
    setPassword("");
    setRoleId(admin.role?._id);
    setDialogTitle("Edit admin user");
    setShowPassword(false);
    setErrors([]);
    setOpenDialog(true);
  };

  const columns = useMemo(
    () => ["ID", "Email", "Admin role", "Edit", "Delete"],
    [],
  );

  const obscureEmail = (email) => {
    const [name, domain] = email.split("@");
    return `${name[0]}${new Array(name.length - 1).join("*")}${
      name[name.length - 1]
    }@${domain[0]}${new Array(domain.length - 1).join("*")}${
      domain[domain.length - 1]
    }`;
  };

  const checkSuperAdmin = () => {
    for (let i = 0; i < roles.length; i++) {
      if (roles[i]._id === roleId && roles[i].name === "super admin") {
        return true;
      }
    }
    return false;
  };

  return (
    <React.Fragment>
      <div className="page-content dark:bg-darkBg1 dark:text-[#c7d8eb]">
        <Container fluid>
          {/* Render Breadcrumb */}
          <div className="p-6 flex flex-col gap-6 ">
            <h1 className="text-2xl dark:text-[#c7d8eb]">Manage admin users</h1>
            <button
              onClick={() => handelDialogNew()}
              className="w-fit rounded-md font-bold text-lg px-4 py-2 bg-primaryColor text-white"
            >
              Add new
            </button>
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white rounded-md">
              <div className="overflow-x-auto ">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`w-1/5 min-w-[130px] content-center border-b border-gray-500 dark:border-white break-words py-2 text-center font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>
                {admins &&
                  admins.map((admin, index) => (
                    <>
                      <div
                        key={`admin-desktop-${index}`}
                        className="hidden lg:flex"
                      >
                        <div className="w-1/5 min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {admin._id}
                        </div>
                        <div className="w-1/5 min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {obscureEmail(admin.email)}
                        </div>
                        <div className="w-1/5 min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {admin.role?.name}
                        </div>
                        <div className="w-1/5 min-w-[130px] px-1 flex justify-center items-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <div
                            onClick={() => {
                              handelDialogUpdate(admin);
                            }}
                            className=" text-secondaryColor cursor-pointer size-fit ="
                          >
                            Edit
                          </div>
                        </div>
                        <div className="w-1/5 min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <button
                            onClick={() => {
                              setAdminId(admin._id);
                              setOpenAlertDialog(true);
                            }}
                            className="text-secondaryColor"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div
                        key={`admin-mobile-${index}`}
                        className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white"
                      >
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">ID</div>
                          <div>{admin._id}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Email</div>
                          <div>{obscureEmail(admin.email)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Admin role</div>
                          <div>{admin.role?.name}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Edit</div>
                          <div
                            onClick={() => {
                              handelDialogUpdate(admin);
                            }}
                            className="text-secondaryColor cursor-pointer"
                          >
                            Edit
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 ">
                          <div className="font-bold">Delete</div>
                          <button
                            onClick={() => {
                              setAdminId(admin._id);
                              setOpenAlertDialog(true);
                            }}
                            className="text-secondaryColor"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </>
                  ))}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogContent className="max-w-screen w-[calc(100vw-20px)] lg:ml-[120px] lg:w-[700px] max-h-[calc(100vh-calc(100vh/3))] overflow-auto select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
                <DialogHeader className={"flex flex-row"}>
                  <h2 className="dark:text-[#c7d8eb] min-w-fit">
                    {dialogTitle}
                  </h2>
                  <div className="w-full flex justify-end">
                    <X
                      className="cursor-pointer dark:text-[#c7d8eb]"
                      onClick={() => {
                        setOpenDialog(false);
                      }}
                      size={30}
                    />
                  </div>
                </DialogHeader>
                <hr className="border-t-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)] -mt-3" />
                <div className="flex w-full flex-col">
                  <h2 className="text-xl dark:text-[#c7d8eb]">
                    <label htmlFor="email">Email</label>
                  </h2>
                  <div className="flex w-full justify-between">
                    <input
                      disabled={dialogTitle === "Edit admin user"}
                      id="email"
                      type="email"
                      placeholder="Email"
                      className="w-full px-4 py-3 rounded-sm dark:bg-darkBg1 border border-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)]"
                      value={
                        (dialogTitle === "Edit admin user" &&
                          obscureEmail(email)) ||
                        email
                      }
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-1 mt-1">
                    {errors.email && (
                      <p className="mt-1 text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>
                <div className="flex w-full flex-col">
                  <h2 className="text-xl dark:text-[#c7d8eb]">
                    <label htmlFor="password">Password</label>
                  </h2>
                  <div className="flex relative w-full justify-between">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      className="w-full pl-4 py-3 pr-12 rounded-sm dark:bg-darkBg1 border border-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)]"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    {showPassword ? (
                      <EyeOff
                        className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
                        size={15}
                        onClick={() => setShowPassword(false)}
                      />
                    ) : (
                      <Eye
                        className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
                        size={15}
                        onClick={() => setShowPassword(true)}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-1 mt-1">
                    {errors.password && (
                      <p className="mt-1 text-red-600">{errors.password}</p>
                    )}
                  </div>
                </div>
                <div className="flex w-full flex-col">
                  <h2 className="text-xl dark:text-[#c7d8eb]">
                    <label htmlFor="role">Role</label>
                  </h2>
                  <div className="flex flex-col gap-1 mt-1">
                    {errors.roleId && (
                      <p className="mt-1 text-red-600">{errors.roleId}</p>
                    )}
                  </div>
                  <select
                    disabled={
                      checkSuperAdmin() && dialogTitle !== "Add admin user"
                    }
                    id="role"
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                    className="px-4 py-3 border border-gray-500 dark:border-white rounded-sm dark:bg-darkBg1"
                  >
                    <option value={""} disabled>
                      Select Role
                    </option>
                    {roles.map((role) => (
                      <option value={role._id} key={role._id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <hr className="border-t-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)]" />
                <DialogFooter>
                  <button
                    className={`${
                      (!(email && password && roleId) &&
                        "bg-primaryColorMuted hover:bg-primaryColorMuted") ||
                      "bg-primaryColor hover:bg-primaryColor"
                    } text-white px-4 py-2 rounded-md text-lg font-semibold`}
                    type="submit"
                    disabled={!(email && password && roleId)}
                    onClick={() => {
                      dialogTitle === "Edit admin user"
                        ? updateAdmin()
                        : addAdmin();
                    }}
                  >
                    {dialogTitle === "Edit admin user" ? "Update" : "Add"}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <AlertDialog
              open={openAlertDialog}
              onOpenChange={setOpenAlertDialog}
            >
              <AlertDialogContent className="max-w-screen w-[calc(100vw-20px)] lg:w-[700px] max-h-[calc(100vh-calc(100vh/3))] overflow-auto select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    <h2 className="dark:text-[#c7d8eb] min-w-fit">
                      Are you sure you want to delete this admin
                    </h2>
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    this admin user from the server.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter
                  className={"flex justify-end gap-2 items-center"}
                >
                  <AlertDialogCancel>
                    <div onClick={() => setOpenAlertDialog(false)}>Cancel</div>
                  </AlertDialogCancel>
                  <AlertDialogAction>
                    <div
                      className="mt-0"
                      onClick={() => handleRoleDelete(adminId)}
                    >
                      Continue
                    </div>
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ManageAdminUsers;
